<template>
    <div class="add-patent">
        <ElAutocomplete
            v-model="inputVal"
            class="add-patent-input"
            popperClass="add-patent-input__popper"
            :fetchSuggestions="searchPatent"
            :selectWhenUnmatched="false"
            :placeholder="placeholder"
        >
            <template #default="{ item }">
                <slot :item="item">
                    <div class="pa-flex pa-min-h-[56px] pa-items-center pa-justify-center">
                        <div v-if="isLoading">
                            <PtIcon icon="SolidLoadingBars" />
                        </div>
                        <div v-else-if="isNodata" class="pa-flex pa-flex-col pa-items-center pa-justify-center">
                            <PtIcon icon="SolidNoDataTip" />
                            <span class="pa-leading-6 pa-text-gray-60">{{ t('view.table.noData') }}</span>
                        </div>
                        <div
                            v-else
                            class="pa-flex pa-w-full pa-items-center pa-overflow-hidden"
                            @click="selectPatent(item)"
                        >
                            <div class="pa-flex-1 pa-overflow-hidden">
                                <div class="pa-flex pa-min-h-[24px] pa-flex-wrap pa-items-center">
                                    <span class="pa-mr-1 pa-leading-6 pa-text-green-600">{{ item.PN }}</span>
                                    <Legal
                                        class="add-patent__legal pa-leading-6"
                                        :legalStatus="item.LEGAL_STATUS"
                                    />
                                </div>
                                <p class="pa-truncate pa-leading-6" :title="item.TITLE" v-html="item.TITLE" />
                            </div>
                            <div
                                v-if="showCompareButton"
                                class="compare-button pa-ml-2 pa-inline-flex pa-h-[32px] pa-shrink-0 pa-cursor-pointer pa-items-center pa-justify-center pa-rounded pa-p-[1px]"
                            >
                                <button
                                    class="compare-button__inner pa-flex pa-h-full pa-w-full pa-items-center pa-justify-center pa-rounded pa-bg-white pa-pl-2 pa-pr-4 pa-leading-6 "
                                >
                                    <PtIcon icon="CompareGradient" class="pa-mr-1" />
                                    <span>{{ t('compare.compareButton') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </slot>
            </template>
            <template #suffix>
                <PtIcon icon="SolidSearchLeft" />
            </template>
        </ElAutocomplete>
    </div>
</template>

<script setup lang="ts">
import { ref, defineProps, withDefaults, defineEmits } from 'vue';
import { Autocomplete } from 'element-ui';
import { useI18n } from '@/composables/use-i18n';
import { Icon as PtIcon } from 'patsnap-biz';
import Legal from '@/components/legal';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import {
    SearchComparePatent,
    type ComparePatentInfo,
} from '@/infrastructure/api-functions/core-search-api/core-search-api';
import _ from 'lodash';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import '@patsnap-ui/icon/assets/solid/SearchLeft.svg';
import '@patsnap-ui/icon/assets/solid/NoDataTip.svg';

const ElAutocomplete = Autocomplete;
const { t } = useI18n();

withDefaults(defineProps<{
    placeholder?: string;
    showCompareButton?: boolean;
}>(), {
    placeholder: '',
    showCompareButton: false,
});

const emit = defineEmits(['submit']);

const inputVal = ref('');
const isLoading = ref(false);
const isNodata = ref(false);
const patentInfo = ref<ComparePatentInfo>();

const searchPatent = async (queryString:string, cb:(info: ComparePatentInfo[]) => void) => {
    const query = (queryString || '').trim();
    if (!query) return cb([]);

    isLoading.value = true;
    cb([{ isLoading: true }]);

    const service = injectService<SearchComparePatent>(SearchComparePatent);
    const data = await service.searchComparePatent({
        q: query,
        simple: true,
    });
    patentInfo.value = data.patent_info;
    isLoading.value = false;
    isNodata.value = _.isEmpty(patentInfo.value);
    if (isNodata.value) return cb([{ isNodata: true }]);

    cb([patentInfo.value]);
};

const selectPatent = (patent:ComparePatentInfo) => {
    const _patentInfo = patent || patentInfo.value;
    if (!_patentInfo) return;
    emit('submit', _patentInfo);
};
</script>

<style lang="scss">
.add-patent-input {
    @apply pa-w-full;
    .el-input__inner {
        @apply pa-border-gray-55 pa-text-gray-1100 pa-h-8 pa-leading-6 pa-px-3 focus:pa-border-green-500;
    }

    .el-input__suffix-inner {
        @apply pa-inline-flex pa-h-full pa-items-center;
    }

    .compare-button {
        background-image: linear-gradient(0deg, #0a3dff, #0dacba, #18ec81);
        &:not(.compare-button__disabled) .compare-button__inner:hover {
            @apply pa-bg-gray-40;
        }
        &:not(.compare-button__disabled) .compare-button__inner:active {
            @apply pa-bg-gray-60;
        }
    }
}

.add-patent-input__popper {
    @apply pa-mt-1 pa-mb-1 #{!important};
    .popper__arrow {
        @apply pa-hidden #{!important};
    }

    .el-autocomplete-suggestion__wrap {
        @apply pa-py-1 pa-px-1 #{!important};
        .el-autocomplete-suggestion__list li {
            @apply pa-text-gray-1100 pa-px-1 hover:pa-bg-gray-20;
        }
    }

    .add-patent__legal.legal-status__container {
        .legal-tag {
            @apply pa-rounded pa-mr-1 pa-leading-4;
        }
    }
}
</style>
